import {
  BBox,
  Feature,
  Geometry,
  LineString,
  MultiLineString,
  MultiPoint,
  MultiPolygon,
  Point,
  Polygon,
  Position,
} from "geojson";
import { bbox as calcBbox } from "@turf/bbox";
import { booleanPointInPolygon } from "@turf/boolean-point-in-polygon";
import { booleanPointOnLine as isPointOnLine } from "@turf/boolean-point-on-line";
import { getGeom } from "@turf/invariant";
import { feature, featureCollection, lineString } from "@turf/helpers";
import { lineSplit } from "@turf/line-split";

/**
 * Tests whether geometry a contains geometry b.
 * The interiors of both geometries must intersect, and the interior and boundary of geometry b must not intersect the exterior of geometry a.
 * booleanContains(a, b) is equivalent to booleanWithin(b, a)
 *
 * @function
 * @param {Geometry|Feature<any>} feature1 GeoJSON Feature or Geometry
 * @param {Geometry|Feature<any>} feature2 GeoJSON Feature or Geometry
 * @returns {boolean} true/false
 * @example
 * var line = turf.lineString([[1, 1], [1, 2], [1, 3], [1, 4]]);
 * var point = turf.point([1, 2]);
 *
 * turf.booleanContains(line, point);
 * //=true
 */
function booleanContains(
  feature1: Feature<any> | Geometry,
  feature2: Feature<any> | Geometry
) {
  const geom1 = getGeom(feature1);
  const geom2 = getGeom(feature2);
  const type1 = geom1.type;
  const type2 = geom2.type;
  const coords1 = geom1.coordinates;
  const coords2 = geom2.coordinates;

  switch (type1) {
    case "Point":
      switch (type2) {
        case "Point":
          return compareCoords(coords1, coords2);
        default:
          throw new Error("feature2 " + type2 + " geometry not supported");
      }
    case "MultiPoint":
      switch (type2) {
        case "Point":
          return isPointInMultiPoint(geom1, geom2);
        case "MultiPoint":
          return isMultiPointInMultiPoint(geom1, geom2);
        default:
          throw new Error("feature2 " + type2 + " geometry not supported");
      }
    case "LineString":
      switch (type2) {
        case "Point":
          return isPointOnLine(geom2, geom1, { ignoreEndVertices: true });
        case "LineString":
          return isLineOnLine(geom1, geom2);
        case "MultiPoint":
          return isMultiPointOnLine(geom1, geom2);
        default:
          throw new Error("feature2 " + type2 + " geometry not supported");
      }
    case "Polygon":
      switch (type2) {
        case "Point":
          return booleanPointInPolygon(geom2, geom1, { ignoreBoundary: true });
        case "LineString":
          return isLineInPoly(geom1, geom2);
        case "Polygon":
          return isPolyInPoly(geom1, geom2);
        case "MultiPoint":
          return isMultiPointInPoly(geom1, geom2);
        case "MultiPolygon":
          return isMultiPolyInPoly(geom1, geom2);
        default:
          throw new Error("feature2 " + type2 + " geometry not supported");
      }
    case "MultiPolygon":
      switch (type2) {
        case "Point":
          return isPointInMultiPolygon(geom1, geom2);
        case "MultiPoint":
          return isMultiPointInMultiPolygon(geom1, geom2);
        case "LineString":
          return isLineInMultiPolygon(geom1, geom2);
        case "MultiLineString":
          return isMultiLineStringInMultiPolygon(geom1, geom2);
        case "Polygon":
          return isPolygonInMultiPolygon(geom1, geom2);
        case "MultiPolygon":
          return isMultiPolygonInMultiPolygon(geom1, geom2);
        default:
          throw new Error("feature2 " + type2 + " geometry not supported");
      }
    default:
      throw new Error("feature1 " + type1 + " geometry not supported");
  }
}

function isPolygonInMultiPolygon(multiPolygon: MultiPolygon, polygon: Polygon) {
  // Compute the polygon's bbox once instead of once per member polygon
  const polygonBbox = calcBbox(polygon);
  return multiPolygon.coordinates.some((coords) =>
    isPolyInPoly({ type: "Polygon", coordinates: coords }, polygon, polygonBbox)
  );
}

/**
 * Is Point inside MultiPolygon
 *
 * @private
 * @param {MultiPolygon} multiPolygon MultiPolygon geometry
 * @param {Point} point Point geometry
 * @returns {boolean} true if point is inside the interior of any polygon in the MultiPolygon
 */
function isPointInMultiPolygon(multiPolygon: MultiPolygon, point: Point) {
  // booleanPointInPolygon supports MultiPolygon natively - a single call is
  // significantly cheaper than one wrapped call per member polygon
  return booleanPointInPolygon(point, multiPolygon, { ignoreBoundary: true });
}

/**
 * Is MultiPoint inside MultiPolygon
 *
 * @private
 * @param {MultiPolygon} multiPolygon MultiPolygon geometry
 * @param {MultiPoint} multiPoint MultiPoint geometry
 * @returns {boolean} true if no point is outside the MultiPolygon and at least one point is in the interior of some polygon
 */
function isMultiPointInMultiPolygon(
  multiPolygon: MultiPolygon,
  multiPoint: MultiPoint
) {
  let oneInside = false;
  for (const coord of multiPoint.coordinates) {
    // All points must be inside the MultiPolygon (boundary OK)
    if (!booleanPointInPolygon(coord, multiPolygon)) {
      return false;
    }
    // Track if at least one point is strictly in the interior
    if (!oneInside) {
      oneInside = booleanPointInPolygon(coord, multiPolygon, {
        ignoreBoundary: true,
      });
    }
  }
  // At least one point must be in the interior (not just on boundary)
  return oneInside;
}

/**
 * Is LineString inside MultiPolygon
 *
 * @private
 * @param {MultiPolygon} multiPolygon MultiPolygon geometry
 * @param {LineString} lineString LineString geometry
 * @returns {boolean} true if the LineString is fully contained within a single polygon of the MultiPolygon
 */
function isLineInMultiPolygon(
  multiPolygon: MultiPolygon,
  lineString: LineString
) {
  return multiPolygon.coordinates.some((coords) =>
    isLineInPoly({ type: "Polygon", coordinates: coords }, lineString)
  );
}

/**
 * Is MultiLineString inside MultiPolygon
 *
 * @private
 * @param {MultiPolygon} multiPolygon MultiPolygon geometry
 * @param {MultiLineString} multiLineString MultiLineString geometry
 * @returns {boolean} true if no LineString has any part in the MultiPolygon's exterior (boundary OK) and at least one LineString has a segment in the interior of some polygon
 */
function isMultiLineStringInMultiPolygon(
  multiPolygon: MultiPolygon,
  multiLineString: MultiLineString
) {
  let oneInterior = false;
  for (const lineCoords of multiLineString.coordinates) {
    const line: LineString = { type: "LineString", coordinates: lineCoords };
    // Each line must not touch the exterior of any polygon it overlaps —
    // lines lying entirely on a polygon's boundary are OK
    let lineStatus: "outside" | "boundary" | "interior" = "outside";
    for (const polyCoords of multiPolygon.coordinates) {
      const status = lineInPolyStatus(
        { type: "Polygon", coordinates: polyCoords },
        line
      );
      if (status === "interior") {
        lineStatus = status;
        break;
      }
      if (status === "boundary") {
        lineStatus = status;
      }
    }
    if (lineStatus === "outside") {
      return false;
    }
    // At least one line must have a segment strictly in some polygon's interior
    if (lineStatus === "interior") {
      oneInterior = true;
    }
  }
  return oneInterior;
}

/**
 * Is MultiPolygon inside MultiPolygon
 *
 * @private
 * @param {MultiPolygon} multiPolygon1 MultiPolygon geometry (container)
 * @param {MultiPolygon} multiPolygon2 MultiPolygon geometry (contained)
 * @returns {boolean} true if every polygon of multiPolygon2 is fully contained within some single polygon of multiPolygon1
 */
function isMultiPolygonInMultiPolygon(
  multiPolygon1: MultiPolygon,
  multiPolygon2: MultiPolygon
) {
  for (const poly2Coords of multiPolygon2.coordinates) {
    const poly2: Polygon = { type: "Polygon", coordinates: poly2Coords };
    // Compute the candidate polygon's bbox once instead of per member polygon
    const poly2Bbox = calcBbox(poly2);
    const polyInside = multiPolygon1.coordinates.some((poly1Coords) =>
      isPolyInPoly(
        { type: "Polygon", coordinates: poly1Coords },
        poly2,
        poly2Bbox
      )
    );
    if (!polyInside) {
      return false;
    }
  }
  return true;
}

function isMultiPolyInPoly(polygon: Polygon, multiPolygon: MultiPolygon) {
  return multiPolygon.coordinates.every((coords) =>
    isPolyInPoly(polygon, { type: "Polygon", coordinates: coords })
  );
}

function isPointInMultiPoint(multiPoint: MultiPoint, pt: Point) {
  let i;
  let output = false;
  for (i = 0; i < multiPoint.coordinates.length; i++) {
    if (compareCoords(multiPoint.coordinates[i], pt.coordinates)) {
      output = true;
      break;
    }
  }
  return output;
}

function isMultiPointInMultiPoint(
  multiPoint1: MultiPoint,
  multiPoint2: MultiPoint
) {
  for (const coord2 of multiPoint2.coordinates) {
    let matchFound = false;
    for (const coord1 of multiPoint1.coordinates) {
      if (compareCoords(coord2, coord1)) {
        matchFound = true;
        break;
      }
    }
    if (!matchFound) {
      return false;
    }
  }
  return true;
}

function isMultiPointOnLine(lineString: LineString, multiPoint: MultiPoint) {
  let haveFoundInteriorPoint = false;
  for (const coord of multiPoint.coordinates) {
    // Membership check first so points not on the line exit early
    if (!isPointOnLine(coord, lineString)) {
      return false;
    }
    // Only probe for an interior point until one has been found
    if (
      !haveFoundInteriorPoint &&
      isPointOnLine(coord, lineString, { ignoreEndVertices: true })
    ) {
      haveFoundInteriorPoint = true;
    }
  }
  return haveFoundInteriorPoint;
}

function isMultiPointInPoly(polygon: Polygon, multiPoint: MultiPoint) {
  let oneInside = false;
  for (const coord of multiPoint.coordinates) {
    // All points must be inside polygon (boundary OK)
    if (!booleanPointInPolygon(coord, polygon)) {
      return false;
    }
    // Track if at least one point is strictly in the interior
    if (!oneInside) {
      oneInside = booleanPointInPolygon(coord, polygon, {
        ignoreBoundary: true,
      });
    }
  }
  // At least one point must be in the interior (not just on boundary)
  return oneInside;
}

function isLineOnLine(lineString1: LineString, lineString2: LineString) {
  let haveFoundInteriorPoint = false;
  const coordinates = lineString2.coordinates;
  for (let i = 0; i < coordinates.length; i++) {
    const coords = coordinates[i];
    // Membership check first so vertices not on the line exit early
    if (!isPointOnLine(coords, lineString1)) {
      return false;
    }
    // Only probe for an interior point until one has been found
    if (!haveFoundInteriorPoint) {
      if (isPointOnLine(coords, lineString1, { ignoreEndVertices: true })) {
        haveFoundInteriorPoint = true;
      } else if (i > 0) {
        // A segment whose endpoints are both on lineString1 (e.g. lineString2's
        // vertices coincide with lineString1's boundary) still shares interior
        // with lineString1. Probe the segment midpoint so an interior overlap
        // is detected even when no vertex of lineString2 is strictly interior.
        const midpoint: Position = [
          (coordinates[i - 1][0] + coords[0]) / 2,
          (coordinates[i - 1][1] + coords[1]) / 2,
        ];
        if (isPointOnLine(midpoint, lineString1, { ignoreEndVertices: true })) {
          haveFoundInteriorPoint = true;
        }
      }
    }
  }
  return haveFoundInteriorPoint;
}

function splitLineIntoSegmentsOnPolygon(
  linestring: LineString,
  polygon: Polygon
) {
  const coords = linestring.coordinates;

  const outputSegments: Feature<LineString>[] = [];

  for (let i = 0; i < coords.length - 1; i++) {
    const seg = lineString([coords[i], coords[i + 1]]);
    const split = lineSplit(seg, feature(polygon));

    if (split.features.length === 0) {
      outputSegments.push(seg);
    } else {
      outputSegments.push(...split.features);
    }
  }

  return featureCollection(outputSegments);
}

function isLineInPoly(polygon: Polygon, linestring: LineString) {
  return lineInPolyStatus(polygon, linestring) === "interior";
}

/**
 * Determines how a LineString relates to a Polygon:
 * - "outside" if any part of the line lies in the polygon's exterior
 * - "interior" if no part lies in the exterior and at least one segment lies in the interior
 * - "boundary" if the entire line lies on the polygon's boundary
 *
 * @private
 * @param {Polygon} polygon Polygon geometry
 * @param {LineString} linestring LineString geometry
 * @returns {"outside"|"boundary"|"interior"} the line's relation to the polygon
 */
function lineInPolyStatus(
  polygon: Polygon,
  linestring: LineString
): "outside" | "boundary" | "interior" {
  const polyBbox = calcBbox(polygon);
  const lineBbox = calcBbox(linestring);

  if (!doBBoxOverlap(polyBbox, lineBbox)) {
    return "outside";
  }

  for (const coord of linestring.coordinates) {
    if (!booleanPointInPolygon(coord, polygon)) {
      return "outside";
    }
  }

  let hasInteriorSegment = false;
  // split intersecting segments and verify their inclusion
  const lineSegments = splitLineIntoSegmentsOnPolygon(linestring, polygon);

  for (const lineSegment of lineSegments.features) {
    const midpoint = getMidpoint(
      lineSegment.geometry.coordinates[0],
      lineSegment.geometry.coordinates[1]
    );

    // make sure all segments do not intersect with polygon exterior
    if (!booleanPointInPolygon(midpoint, polygon)) {
      return "outside";
    }

    // track whether at least 1 segment intersects with the polygon's interior
    if (
      !hasInteriorSegment &&
      booleanPointInPolygon(midpoint, polygon, { ignoreBoundary: true })
    ) {
      hasInteriorSegment = true;
    }
  }

  return hasInteriorSegment ? "interior" : "boundary";
}

/**
 * Is Polygon2 in Polygon1
 * Only takes into account outer rings
 *
 * @private
 * @param {Geometry|Feature<Polygon>} feature1 Polygon1
 * @param {Geometry|Feature<Polygon>} feature2 Polygon2
 * @returns {boolean} true/false
 */
function isPolyInPoly(
  feature1: Feature<Polygon> | Polygon,
  feature2: Feature<Polygon> | Polygon,
  feature2Bbox?: BBox
) {
  // Handle Nulls
  if (feature1.type === "Feature" && feature1.geometry === null) {
    return false;
  }
  if (feature2.type === "Feature" && feature2.geometry === null) {
    return false;
  }

  const poly1Bbox = calcBbox(feature1);
  const poly2Bbox = feature2Bbox ?? calcBbox(feature2);
  if (!doBBoxOverlap(poly1Bbox, poly2Bbox)) {
    return false;
  }

  const poly1 = getGeom(feature1);
  const coords = getGeom(feature2).coordinates;
  for (const ring of coords) {
    for (const coord of ring) {
      if (!booleanPointInPolygon(coord, feature1)) {
        return false;
      }
    }
    // Having every vertex inside feature1 is not sufficient when feature1 is concave: an edge of
    // feature2 can still cross feature1's exterior (e.g. through a notch) while all its vertices
    // remain inside. Split each edge of feature2 on feature1's boundary and reject if any resulting
    // sub-segment lies (via its midpoint) strictly in feature1's exterior (#2242).
    const segments = splitLineIntoSegmentsOnPolygon(
      { type: "LineString", coordinates: ring },
      poly1
    );
    for (const segment of segments.features) {
      const midpoint = getMidpoint(
        segment.geometry.coordinates[0],
        segment.geometry.coordinates[1]
      );
      if (
        !booleanPointInPolygon(midpoint, feature1) &&
        !isPointOnPolygonBoundary(midpoint, poly1)
      ) {
        return false;
      }
    }
  }
  return true;
}

// Distance under which a point is treated as lying on a polygon boundary,
// expressed in the coordinate units of the input (degrees for WGS84 lng/lat).
// GeoJSON coordinates are only meaningful to about 6 decimal places, roughly
// 10 cm on the ground, per RFC 7946 section 11.2, so anything closer than this
// to an edge is treated as coincident with it. This is a real distance, unlike
// a raw cross-product epsilon which scales with edge length and coordinate
// magnitude and so is not scale invariant.
// https://datatracker.ietf.org/doc/html/rfc7946#section-11.2
const BOUNDARY_DISTANCE_TOLERANCE = 1e-6;

/**
 * Shortest distance from a point to a line segment on the plane, in the
 * coordinate units of the inputs.
 *
 * @private
 * @param {Position} point point [x, y]
 * @param {Position} start segment start [x, y]
 * @param {Position} end segment end [x, y]
 * @returns {number} distance from the point to the segment
 */
function pointToSegmentDistance(
  point: Position,
  start: Position,
  end: Position
): number {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const segmentLengthSquared = dx * dx + dy * dy;

  // Project the point onto the segment, clamping to the endpoints. A
  // zero-length segment collapses to its shared endpoint (t stays 0).
  let t = 0;
  if (segmentLengthSquared > 0) {
    t =
      ((point[0] - start[0]) * dx + (point[1] - start[1]) * dy) /
      segmentLengthSquared;
    t = Math.max(0, Math.min(1, t));
  }

  const closestX = start[0] + t * dx;
  const closestY = start[1] + t * dy;
  const offsetX = point[0] - closestX;
  const offsetY = point[1] - closestY;
  return Math.sqrt(offsetX * offsetX + offsetY * offsetY);
}

/**
 * Whether a point lies on the boundary of a polygon, within
 * BOUNDARY_DISTANCE_TOLERANCE.
 * Used to distinguish edges that run along the polygon boundary (allowed for containment) from
 * edges that cross into the polygon's exterior. The tolerance absorbs the floating-point
 * error introduced when splitting boundary-coincident edges, while staying far smaller than any
 * real excursion into the exterior.
 *
 * @private
 * @param {Position} point point [x, y]
 * @param {Polygon} polygon polygon geometry
 * @returns {boolean} true if the point lies on any ring of the polygon
 */
function isPointOnPolygonBoundary(point: Position, polygon: Polygon): boolean {
  return polygon.coordinates.some((ring) => {
    for (let i = 0; i < ring.length - 1; i++) {
      if (
        pointToSegmentDistance(point, ring[i], ring[i + 1]) <=
        BOUNDARY_DISTANCE_TOLERANCE
      ) {
        return true;
      }
    }
    return false;
  });
}

function doBBoxOverlap(bbox1: BBox, bbox2: BBox) {
  if (bbox1[0] > bbox2[0]) {
    return false;
  }
  if (bbox1[2] < bbox2[2]) {
    return false;
  }
  if (bbox1[1] > bbox2[1]) {
    return false;
  }
  if (bbox1[3] < bbox2[3]) {
    return false;
  }
  return true;
}

/**
 * compareCoords
 *
 * @private
 * @param {Position} pair1 point [x,y]
 * @param {Position} pair2 point [x,y]
 * @returns {boolean} true/false if coord pairs match
 */
function compareCoords(pair1: number[], pair2: number[]) {
  return pair1[0] === pair2[0] && pair1[1] === pair2[1];
}

function getMidpoint(pair1: number[], pair2: number[]) {
  return [(pair1[0] + pair2[0]) / 2, (pair1[1] + pair2[1]) / 2];
}

export {
  booleanContains,
  isPolygonInMultiPolygon,
  isPointInMultiPoint,
  isMultiPointInMultiPoint,
  isMultiPointOnLine,
  isMultiPointInPoly,
  isLineOnLine,
  isLineInPoly,
  isPolyInPoly,
  isMultiPolyInPoly,
  doBBoxOverlap,
  compareCoords,
  getMidpoint,
};

export default booleanContains;
